'use client'
import HotelCenteredMessage from '@/src/components/common/hotels/hotelCenteredMessage/hotelCenteredMessage'
import HotelHeaderCard from '@/src/components/common/hotels/review/hotelHeaderCard'
import HotelInfoSections from '@/src/components/common/hotels/review/hotelInfoSections'
import ImageGallery from '@/src/components/common/hotels/review/imageGallery'
import MapSection from '@/src/components/common/hotels/review/mapSection'
import RoomSelectionSection from '@/src/components/common/hotels/review/roomSelectionSection'
import DetailsShimmer from '@/src/components/common/hotels/review/detailsShimmer'
import ReviewTabBar, { ReviewTab } from '@/src/components/mobile/hotels/review/reviewTabBar'
import { HotelReviewViewModel } from '@/src/types/hotels/details'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box, IconButton } from '@mui/material'
import { useState } from 'react'

export interface HotelDetailsMobileProps {
    viewModel: HotelReviewViewModel | null
    isLoading: boolean
    isError: boolean
    errorTitle: string
    errorSubtitle: string
    errorCtaLabel: string
    tabLabels: Record<ReviewTab, string>
    photosCountLabel: (count: number) => string
    viewPhotosLabel: string
    /** Stay-date picker (modal/drawer, own logic); opened by the header card's date row. */
    datePicker?: React.ReactNode
    onEditDates?: () => void
    /**
     * Tabs to offer, in order. Defaults to all three. The property page drops 'rooms'
     * until the guest has searched, because there are no prices to show yet.
     */
    availableTabs?: ReviewTab[]
    /** Tab selected on first render. Defaults to the first available one. */
    defaultTab?: ReviewTab
    /** Render the info sections open, for pages entered from a search engine. */
    expandAll?: boolean
    /** Labels the search control instead of showing a bare icon. */
    searchLabel?: string
    /** Runs the search. Only the property (SEO) page supplies one. */
    onSearch?: () => void
    /** Rooms-and-guests control. Only the property (SEO) page supplies one. */
    guestsField?: React.ReactNode
    /** Trail rendered above the content. The booking flow has none — it is entered from a
     *  search the guest already ran, not from a place in the site's hierarchy. */
    breadcrumb?: React.ReactNode
    onBack: () => void
    onSelectRoom: (rId: string, arNo: string) => void
}

// Mobile hotel review/details page — 1:1 port of the RN hotelReview screen:
// carousel → hotel info card → sticky tab bar → Select Room / Hotel Info / Map.
const ALL_REVIEW_TABS: ReviewTab[] = ['rooms', 'info', 'map']

const HotelDetailsMobile: React.FC<HotelDetailsMobileProps> = ({
    viewModel,
    isLoading,
    isError,
    errorTitle,
    errorSubtitle,
    errorCtaLabel,
    tabLabels,
    photosCountLabel,
    viewPhotosLabel,
    datePicker,
    onEditDates,
    availableTabs,
    defaultTab,
    expandAll = false,
    searchLabel,
    onSearch,
    guestsField,
    breadcrumb,
    onBack,
    onSelectRoom
}) => {
    const [activeTab, setActiveTab] = useState<ReviewTab>(defaultTab ?? (availableTabs ?? ALL_REVIEW_TABS)[0])
    const [lastDefaultTab, setLastDefaultTab] = useState(defaultTab)

    // Follow the tab the page asks for when it changes: the property page moves to rooms
    // once a search has run, rather than leaving the guest on the overview.
    if (defaultTab && defaultTab !== lastDefaultTab) {
        setLastDefaultTab(defaultTab)
        setActiveTab(defaultTab)
    }

    if (isLoading) {
        return <DetailsShimmer variant="mobile" />
    }

    if (isError || !viewModel) {
        return (
            <HotelCenteredMessage
                title={errorTitle}
                subtitle={errorSubtitle}
                actionLabel={errorCtaLabel}
                onAction={onBack}
                minHeight="100dvh"
            />
        )
    }

    const reviewContext = {
        productId: viewModel.productId,
        uid: viewModel.uid,
        echoToken: viewModel.echoToken
    }

    const tabs = (availableTabs ?? ALL_REVIEW_TABS).map(key => ({ key, label: tabLabels[key] }))

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'neutral.100' }}>
            {breadcrumb && <Box sx={{ px: 2, bgcolor: 'neutral.white' }}>{breadcrumb}</Box>}
            <Box sx={{ position: 'relative' }}>
                <ImageGallery
                    variant="mobile"
                    images={viewModel.hotel.images}
                    alt={`${viewModel.hotel.name}, ${viewModel.hotel.cityName}`}
                    photosCountLabel={photosCountLabel}
                    viewPhotosLabel={viewPhotosLabel}
                    galleryCategories={viewModel.hotel.galleryCategories}
                />
                <IconButton
                    onClick={onBack}
                    aria-label="Go back"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        insetInlineStart: 12,
                        bgcolor: 'rgba(0,0,0,0.35)',
                        color: 'neutral.white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                        '[dir="rtl"] &': { transform: 'scaleX(-1)' }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            <HotelHeaderCard
                variant="compact"
                hotel={viewModel.hotel}
                stay={viewModel.stay}
                onEditDates={onEditDates}
                searchLabel={searchLabel}
                onSearch={onSearch}
                guestsField={guestsField}
            />

            {datePicker}

            <Box sx={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <ReviewTabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </Box>

            <Box sx={{ p: 2 }}>
                {activeTab === 'rooms' && (
                    <RoomSelectionSection
                        roomGroups={viewModel.roomGroups}
                        variant="mobile"
                        reviewContext={reviewContext}
                        onSelect={onSelectRoom}
                    />
                )}
                {activeTab === 'info' && (
                    <HotelInfoSections
                        info={viewModel.info}
                        tags={viewModel.hotel.tags}
                        numOfRooms={viewModel.hotel.numOfRooms}
                        numOfFloors={viewModel.hotel.numOfFloors}
                        expandAll={expandAll}
                    />
                )}
                {activeTab === 'map' && (
                    <MapSection
                        geo={viewModel.hotel.geo}
                        hotelName={viewModel.hotel.name}
                        address={viewModel.hotel.address}
                    />
                )}
            </Box>
        </Box>
    )
}

export { HotelDetailsMobile }
export default HotelDetailsMobile
